"use client";

// Kartu "Hadiah untukmu" di keranjang (hadiah pembelian, mig 8680; rencana
// §6.3). Sejak putusan pemilik 2026-10-28 (§15.8) ia menempel di bawah baris
// varian syaratnya (`nested`); kartu sendiri hanya bila barisnya tak tampil.
// Hadiah tak pernah melebur dengan baris biasa dan jumlahnya mengikuti
// hak — pembeli hanya memilih variannya (bila tak dikunci); hadiah tak bisa
// dilewati (H40, mig 8860), pcs yang belum dipilih diisi varian bawaan.
// Varian ber-Harga Tambahan tetap dipajang; mengetuknya memunculkan alasan
// (H1). Hak dijepit sisa kuota hadiah (H15, mig 8740). Syarat nilai belanja
// (mig 8780) mendorong "Tambah Rp X lagi". Sisa pesanan sebelumnya ikut
// dihitung (H55, mig 8880): hadiah yang tercapai karenanya ditambahkan server
// sesudah Keep Produk, tanpa pemilih varian. Pembelian lama aturan kelipatan
// yang masih bisa bertambah juga (Hadiah Tahap 6-2): hadiahnya bertambah di
// pesanan sebelumnya. Hitungan di `src/lib/giftCart.ts`.

import { Gift, Minus, Plus } from "lucide-react";
import { useState } from "react";

import { StoreImage } from "@/components/StoreImage";
import { giftRemainingLabel, giftRewardBlockText } from "@/lib/gift";
import { stepGiftClaim, type GiftCartEntry } from "@/lib/giftCart";
import { formatPrice } from "@/lib/pricing";

export function GiftCartCard({
  entry,
  nested = false,
  onClaimChange
}: {
  entry: GiftCartEntry;
  /**
   * Menempel di bawah baris varian syaratnya (putusan pemilik 2026-10-28,
   * RENCANA-hadiah-pembelian §15.8) — tanpa bingkai kartu sendiri.
   */
  nested?: boolean;
  onClaimChange: (lines: GiftCartEntry["claim"]) => void;
}) {
  const [warning, setWarning] = useState("");
  const { reward, status } = entry;
  const claimed = entry.claim.reduce((s, l) => s + l.qty, 0);
  const qtyOf = (variantId: string) => entry.claim.find((l) => l.variantId === variantId)?.qty ?? 0;
  // Pembeli memilih bila tersisa lebih dari satu varian yang bisa diberikan.
  const choosing = status === "siap" && !entry.locked && entry.choices.filter((c) => c.selectable).length > 1;
  // "Sisa 3 pcs" hanya saat tinggal sedikit; hak yang terpotong kuota punya kalimatnya sendiri.
  const remainingLabel = !entry.quotaCapped && status !== "kuota_habis" ? giftRemainingLabel(entry.remaining) : null;
  // Kekurangan menuju hak berikutnya: pcs (ukuran pcs) atau rupiah (ukuran nilai belanja).
  const shortfall =
    entry.missing > 0 ? `${entry.missing} pcs` : entry.missingAmount > 0 ? formatPrice(entry.missingAmount) : null;
  // Sisa pesanan sebelumnya yang ikut dihitung (H55).
  const carryText =
    status === "kuota_habis" || entry.carryPcs <= 0
      ? null
      : entry.measure === "nilai"
        ? `Termasuk belanja ${formatPrice(entry.carryValue)} dari pesanan sebelumnya.`
        : `Termasuk ${entry.carryPcs} pcs dari pesanan sebelumnya.`;
  // Barang ini / sisa pesanan sebelumnya melengkapi pembelian lama (Tahap 6-2).
  const oldText =
    entry.oldPcs > 0 ? `Hadiah ${entry.oldPcs} pcs ditambahkan ke pesanan sebelumnya sesudah kamu ketuk Keep Produk.` : null;

  const tap = (variantId: string, delta: 1 | -1) => {
    const choice = entry.choices.find((c) => c.id === variantId);
    if (choice && !choice.selectable) {
      setWarning(choice.block ? giftRewardBlockText(choice.block) : "");
      return;
    }
    setWarning("");
    onClaimChange(stepGiftClaim(entry, variantId, delta));
  };

  return (
    <div className={nested ? "rounded-lg bg-shopeeSoft/30 p-2.5" : "rounded-xl border border-shopee/30 bg-shopeeSoft/30 p-3"}>
      <div className="flex items-center justify-between gap-2">
        <p className="inline-flex items-center gap-1 text-[11px] font-extrabold text-shopee">
          <Gift size={13} />
          {status === "kurang" || status === "kuota_habis" ? "Hadiah pembelian" : "Hadiah untukmu"}
        </p>
        {remainingLabel ? (
          <span className="shrink-0 rounded-md bg-white px-1.5 py-0.5 text-[10px] font-extrabold text-shopee">
            {remainingLabel}
          </span>
        ) : null}
      </div>
      <p className="mt-0.5 text-xs font-bold text-ink">{entry.sentence}</p>

      {status === "kurang" && shortfall ? (
        <p className="mt-1 text-[11px] font-semibold text-muted">
          Tambah {shortfall} lagi untuk dapat {entry.nextEntitlement} hadiah.
        </p>
      ) : null}
      {entry.unpricedPcs > 0 && status !== "kuota_habis" ? (
        <p className="mt-1 text-[11px] font-semibold text-muted">
          {entry.unpricedPcs} pcs yang harganya belum ada tidak ikut dihitung.
        </p>
      ) : null}
      {carryText ? <p className="mt-1 text-[11px] font-semibold text-muted">{carryText}</p> : null}

      {status === "otomatis" ? (
        <>
          <div className="mt-2 flex items-center gap-2">
            <GiftThumb src={reward.imageUrl} alt={reward.name} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-bold text-ink">{reward.name}</p>
              <p className="truncate text-[11px] text-muted">
                {entry.locked
                  ? `${entry.locked.name} · ${entry.autoEntitlement + entry.oldPcs} pcs`
                  : `${entry.autoEntitlement + entry.oldPcs} pcs, variannya dipilihkan otomatis`}
              </p>
            </div>
            <span className="shrink-0 rounded-md bg-leaf/10 px-1.5 py-0.5 text-[10px] font-extrabold text-leaf">
              Gratis
            </span>
          </div>
          {oldText ? <p className="mt-1.5 text-[11px] font-semibold text-muted">{oldText}</p> : null}
          {entry.autoEntitlement > 0 ? (
            <p className="mt-1.5 text-[11px] font-semibold text-muted">
              Hadiahnya ditambahkan otomatis sesudah kamu ketuk Keep Produk.
            </p>
          ) : null}
          {shortfall ? (
            <p className="mt-1.5 text-[11px] font-semibold text-muted">
              Tambah {shortfall} lagi, hadiahnya jadi {entry.nextEntitlement} pcs.
            </p>
          ) : null}
        </>
      ) : null}

      {status === "siap" ? (
        <>
          {entry.quotaCapped ? (
            <p className="mt-1 text-[11px] font-semibold text-muted">
              Hadiah untukmu tinggal {entry.remaining} pcs.
            </p>
          ) : null}
          {!choosing
            ? entry.claim.map((l) => {
                const v = entry.choices.find((c) => c.id === l.variantId);
                return (
                  <div key={l.variantId} className="mt-2 flex items-center gap-2">
                    <GiftThumb src={reward.imageUrl} alt={reward.name} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-bold text-ink">{reward.name}</p>
                      <p className="truncate text-[11px] text-muted">
                        {v?.name ?? "Varian hadiah"} · {l.qty} pcs
                      </p>
                    </div>
                    <span className="shrink-0 rounded-md bg-leaf/10 px-1.5 py-0.5 text-[10px] font-extrabold text-leaf">
                      Gratis
                    </span>
                  </div>
                );
              })
            : null}

          {choosing ? (
            <div className="mt-2">
              <div className="flex items-center gap-2">
                <GiftThumb src={reward.imageUrl} alt={reward.name} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-bold text-ink">{reward.name}</p>
                  <p className="text-[11px] text-muted">
                    Pilih varian hadiahnya ({entry.entitlement} pcs, gratis).
                  </p>
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {entry.choices.map((c) => {
                  const qty = qtyOf(c.id);
                  const on = qty > 0;
                  return (
                    <div
                      key={c.id}
                      className={`flex items-center overflow-hidden rounded-lg border text-[11px] font-bold ${
                        on
                          ? "border-shopee bg-white text-shopee"
                          : c.selectable
                            ? "border-neutral-200 bg-white text-ink"
                            : "border-dashed border-neutral-200 bg-white text-muted"
                      }`}
                    >
                      {entry.entitlement > 1 && on ? (
                        <button
                          type="button"
                          aria-label={`Kurangi ${c.name}`}
                          onClick={() => tap(c.id, -1)}
                          className="px-1.5 py-1.5"
                        >
                          <Minus size={11} />
                        </button>
                      ) : null}
                      <button
                        type="button"
                        aria-pressed={on}
                        onClick={() => tap(c.id, 1)}
                        className={`px-2.5 py-1.5 ${c.selectable ? "" : "line-through"}`}
                      >
                        {c.name}
                        {entry.entitlement > 1 && on ? ` ×${qty}` : ""}
                      </button>
                      {entry.entitlement > 1 && on ? (
                        <button
                          type="button"
                          aria-label={`Tambah ${c.name}`}
                          onClick={() => tap(c.id, 1)}
                          className="px-1.5 py-1.5"
                        >
                          <Plus size={11} />
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
              {claimed < entry.entitlement ? (
                <p className="mt-1.5 text-[11px] font-semibold text-muted">
                  Sisa {entry.entitlement - claimed} hadiah dipilihkan otomatis kalau tidak kamu pilih.
                </p>
              ) : null}
            </div>
          ) : null}

          {warning ? <p className="mt-1.5 text-[11px] font-bold text-shopee">{warning}</p> : null}

          {entry.autoEntitlement > 0 ? (
            <p className="mt-1.5 text-[11px] font-semibold text-muted">
              Hadiah {entry.autoEntitlement} pcs lagi ditambahkan otomatis sesudah kamu ketuk Keep Produk.
            </p>
          ) : null}
          {oldText ? <p className="mt-1.5 text-[11px] font-semibold text-muted">{oldText}</p> : null}

          {shortfall ? (
            <p className="mt-1.5 text-[11px] font-semibold text-muted">
              Tambah {shortfall} lagi, hadiahnya jadi {entry.nextEntitlement} pcs.
            </p>
          ) : null}
        </>
      ) : null}

      {status === "habis" ? (
        <p className="mt-1 text-[11px] font-semibold text-muted">
          Stok hadiahnya sedang habis. Pesananmu tetap bisa dibuat tanpa hadiah.
        </p>
      ) : null}

      {status === "kuota_habis" ? (
        <p className="mt-1 text-[11px] font-semibold text-muted">
          Kuota hadiahnya sudah habis. Pesananmu tetap bisa dibuat tanpa hadiah.
        </p>
      ) : null}

      {status === "tidak_bisa_dipesan" ? (
        <p className="mt-1 text-[11px] font-semibold text-muted">
          Hadiahnya sedang tidak bisa dipesan. Pesananmu tetap bisa dibuat tanpa hadiah.
        </p>
      ) : null}
    </div>
  );
}

function GiftThumb({ src, alt }: { src: string | null; alt: string }) {
  return (
    <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-neutral-100">
      {src ? (
        <StoreImage src={src} alt={alt} fill sizes="40px" className="object-cover" />
      ) : (
        <div className="grid h-full w-full place-items-center text-muted">
          <Gift size={16} />
        </div>
      )}
    </div>
  );
}
